import React from "react";

import CalendarIcon from "@uui/CalendarCheck02";
import { filter } from "lodash/fp";

import { Typography, Box, FormControlLabel } from "@mui/material";

import * as UpcomingEventUtils from "@app/analytics/Calendar/UpcomingEvents/utils";
import useEventGroups from "@app/analytics/hooks/calendar/useEventGroups";
import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import { WrapUpCard } from "@app/analytics/onboarding/components/styles";
import { UserEventGroup } from "@app/analytics/types/eventGroupTypes";
import { CONFERENCING_PLATFORMS_BY_ID } from "@app/common/constants/platforms";
import type { ConferencingMeetingPlatform } from "@app/common/types";
import { formatStartAndEndTime } from "@app/common/utils";

import IconBackground from "@rui/IconBackground";
import { Column, Row } from "@rui/Stack";
import { Img } from "@rui/styles";
import Switch from "@rui/Switch";

const EventRow = ({
  event,
  onChange,
}: {
  event: UserEventGroup;
  onChange: (e: React.SyntheticEvent, checked: boolean) => void;
}) => {
  const logo =
    CONFERENCING_PLATFORMS_BY_ID[
      event.meetingPlatform as ConferencingMeetingPlatform
    ]?.icon;

  return (
    <Row
      paddingY={1}
      paddingX={2}
      spacing={2}
      variant="apart"
      sx={{
        "&:not(:last-of-type)": {
          borderBottom: "1px solid",
          borderColor: "shades.neutral.100",
        },
      }}
    >
      <Row variant="left" spacing={2}>
        {logo ? <Img src={logo} height="24px" /> : <Box width="24px" />}
        <Column>
          <Typography variant="bodySecondaryReg" color="shades.neutral.800">
            {event.title}
          </Typography>
          <Typography variant="labelReg" color="shades.neutral.500">
            {event.startTime.weekdayShort} {event.startTime.toFormat("M/d/yy")},{" "}
            {formatStartAndEndTime(event.startTime, event.endTime)}
          </Typography>
        </Column>
      </Row>
      <FormControlLabel
        control={<Switch />}
        checked={UpcomingEventUtils.isReadInvited(event)}
        onChange={onChange}
        label={
          <Typography variant="labelMed" color="shades.neutral.700">
            Add Read
          </Typography>
        }
        labelPlacement="start"
      />
    </Row>
  );
};

const WrapUpUpcomingEvents = () => {
  const { events, updateEvent } = useEventGroups();
  const { userSettings } = useUserSettings();

  const joinPreferencesSetToManual =
    userSettings.calendarPermissionLevel === "invite_only";

  const eventsToShow = filter(UpcomingEventUtils.canInviteRead, events).slice(
    0,
    3
  );

  if (eventsToShow.length === 0 || !joinPreferencesSetToManual) return null;

  const handleToggleAddRead =
    (event: UserEventGroup) => (_e: React.SyntheticEvent, checked: boolean) => {
      updateEvent.mutate({
        id: event.id,
        body: { optIn: checked },
      });
    };

  return (
    <WrapUpCard>
      <IconBackground
        height={48}
        bgcolor="shades.secondary.50"
        padding={8}
        className="wrap-up-card__icon"
      >
        <CalendarIcon sx={{ color: "shades.primary.500" }} />
      </IconBackground>
      <Column spacing={2}>
        <Column spacing={1}>
          <Typography variant="bodyPrimaryMed" color="shades.neutral.700">
            Upcoming calendar events
          </Typography>
          <Typography variant="bodySecondaryReg" color="shades.neutral.600">
            Read detected the following upcoming meetings. Since your join
            preferences are set to manual, you can add Read to any meeting below
            to try it out.
          </Typography>
        </Column>
        <Column>
          {eventsToShow.slice(0, 3).map((event) => (
            <EventRow
              key={event.id}
              event={event}
              onChange={handleToggleAddRead(event)}
            />
          ))}
        </Column>
      </Column>
    </WrapUpCard>
  );
};

export default WrapUpUpcomingEvents;
